import React, { useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import groupBy from 'lodash/groupBy';
import FileItem from 'UI/components/molecules/FileItem';
import FileUploader, { FileUploaderMode } from 'UI/components/molecules/FileUploader';
import { EntityType } from 'UI/constants/entityTypes';

import {
  ATTACHMENTS_VARIANT_PROPS,
  AttachmentsVariant,
  EMPTY_CATEGORY_MESSAGE,
  MAX_FILE_NAME_LENGTH,
  MAX_NUMBER_OF_FILES
} from './Attachments.constants';
import { NoAttachmentsLabel, SubContainer } from './Attachments.styled';
import { getAllowedActions, getResolvedCategories } from './Attachments.utils';

export const Attachments = ({
  acceptFiles,
  allowDelete = true,
  categories = [],
  categoryIdMap = {},
  disabled = false,
  endpoint = '/files',
  fileExplorerSections = [],
  files = [],
  maxFileSize,
  maxNumberOfFiles = MAX_NUMBER_OF_FILES,
  multiple = true,
  namePrefix = 'file_',
  variant = AttachmentsVariant.Create
}) => {
  const groupedFiles = useMemo(
    () =>
      groupBy(
        files.filter(file => file.file_type_id != null),
        'file_type_id'
      ),
    [files]
  );
  const resolvedCategories = useMemo(
    () =>
      getResolvedCategories({
        categories,
        categoryIdMap,
        fileExplorerSections,
        groupedFiles
      }),
    [categories, categoryIdMap, fileExplorerSections, groupedFiles]
  );
  const uploaderVariantProps =
    ATTACHMENTS_VARIANT_PROPS[variant] || ATTACHMENTS_VARIANT_PROPS[AttachmentsVariant.Create];

  const content =
    variant === AttachmentsVariant.Readonly ? (
      <List>
        {resolvedCategories.map(({ category, filesByCategory }) => {
          return (
            <React.Fragment key={category.id}>
              <ListSubheader disableSticky>{category.title}</ListSubheader>
              {filesByCategory.length === 0 && (
                <ListItem dense>
                  <NoAttachmentsLabel color="textSecondary" variant="body2">
                    {EMPTY_CATEGORY_MESSAGE}
                  </NoAttachmentsLabel>
                </ListItem>
              )}
              {filesByCategory.map(attachment => (
                <FileItem
                  key={attachment.id || attachment.name}
                  fileName={attachment?.file_name || attachment?.name}
                  file={attachment}
                  error={!!attachment.hasError}
                  loading={!!attachment.isLoading}
                  maxFileNameLength={MAX_FILE_NAME_LENGTH}
                  message={attachment.message}
                  showFileSize
                  showMenu
                  showSelector={false}
                  allowedActions={getAllowedActions({
                    allowDelete,
                    file: attachment,
                    isReadonlyVariant: true
                  })}
                />
              ))}
            </React.Fragment>
          );
        })}
      </List>
    ) : (
      resolvedCategories.map(({ category, mappedCategory, filesByCategory, filteredSections }) => {
        return (
          <FileUploader
            key={category.id}
            category={mappedCategory}
            categories={[mappedCategory]}
            defaultEntityType={EntityType.Candidate}
            disabled={disabled}
            endpoint={endpoint}
            fileExplorerSections={filteredSections}
            files={filesByCategory}
            maxNumberOfFiles={maxNumberOfFiles}
            maxFileSize={maxFileSize}
            acceptFiles={acceptFiles}
            multiple={multiple}
            mode={FileUploaderMode.ListSelectableManual}
            modeExtraProps={{
              getAllowedActions: file => getAllowedActions({ allowDelete, file }),
              maxFileNameLength: MAX_FILE_NAME_LENGTH
            }}
            name={`${namePrefix}${category.id}`}
            showSelector={uploaderVariantProps.showSelector}
            showUploadingState
            showUploader={uploaderVariantProps.showUploader}
            shouldAppendCategory
          />
        );
      })
    );

  return (
    <Grid container>
      <SubContainer item xs={12}>
        {content}
      </SubContainer>
    </Grid>
  );
};
